import PropTypes from 'prop-types';
import { useState, useMemo, useCallback } from 'react';
import { CheckCircleIcon, ExclamationIcon, XIcon } from '@heroicons/react/outline';
import { CustomButton } from '@/components/common';
import { ModalWrapper } from '@/components/modals';
import ErrorReport from './ErrorReport';
import StepsReport from './StepsReport';
import STEP_RESULT_TYPE from './stepResultType';

const MigrationReport = ({
  report,
  isOpened,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('parsed');

  const isSuccessful = useMemo(() => {
    if (report?.error) {
      return false;
    }

    return report?.is_successful;
  }, [report]);

  const onModalClose = useCallback(() => {
    setActiveTab('parsed');

    onClose();
  }, [onClose]);

  return (
    <ModalWrapper
      isOpened={isOpened}
      onCloseModal={onModalClose}
      width="max-w-4xl w-full"
    >
      {/* TODO: check height */}
      <section className="flex flex-col min-h-[50vh] max-h-[90vh] overflow-hidden">
        <header className="flex items-center py-4 px-6 border-b">
          {isSuccessful ? (
            <>
              <div className="flex justify-center items-center w-10 h-10 mr-2 rounded-full bg-green-200">
                <CheckCircleIcon
                  className="w-6 h-6 stroke-green-600"
                  aria-hidden="true"
                />
              </div>
              <div className="text-2xl font-medium text-gray-900">Migration Successful</div>
            </>
          ) : (
            <>
              <div className="flex justify-center items-center w-10 h-10 mr-2 rounded-full bg-red-200">
                <ExclamationIcon
                  className="w-6 h-6 stroke-red-600"
                  aria-hidden="true"
                />
              </div>
              <div className="text-2xl font-medium text-gray-900">Migration Failed</div>
            </>
          )}
          <button
            type="button"
            className="p-2 -m-2 ml-auto"
            onClick={onClose}
          >
            <XIcon
              className="w-6 h-6 text-gray-500 hover:text-gray-700 cursor-pointer"
              aria-hidden="true"
            />
          </button>
        </header>
        <main className="flex-grow flex flex-col py-4 overflow-hidden">
          <nav className="flex px-6">
            <button
              type="button"
              onClick={() => setActiveTab('parsed')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'parsed'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Parsed
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('raw')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'raw'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Raw
            </button>
          </nav>

          <div className="flex-grow px-6 py-1 mt-4 overflow-auto">
            {activeTab === 'parsed' ? (
              report
                ? (
                  report.error !== undefined
                    ? <ErrorReport report={report} />
                    : <StepsReport report={report} />
                )
                : null
            ) : (
              <pre className="bg-gray-50 border p-2 rounded overflow-auto max-h-96 text-gray-800">
                {JSON.stringify(report, null, 2)}
              </pre>
            )}
          </div>
        </main>
        <footer className="flex items-center justify-end py-4 px-6 border-t">
          <CustomButton
            color="active"
            onClick={onClose}
            className="px-6 py-2 font-normal"
          >
            Close
          </CustomButton>
        </footer>
      </section>
    </ModalWrapper>
  );
};

MigrationReport.propTypes = {
  report: PropTypes.oneOfType([
    PropTypes.shape({
      error: PropTypes.string.isRequired,
      details: PropTypes.shape({
        message: PropTypes.string.isRequired,
        class: PropTypes.string,
        file: PropTypes.string,
      }).isRequired,
    }),
    PropTypes.shape({
      is_successful: PropTypes.bool.isRequired,
      steps: PropTypes.arrayOf(PropTypes.shape({
        is_successful: PropTypes.bool.isRequired,
        type: PropTypes.oneOf(STEP_RESULT_TYPE.values()).isRequired,
        entity: PropTypes.string.isRequired,
        key: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
        context: PropTypes.arrayOf(PropTypes.string).isRequired,
        dependentEntities: PropTypes.arrayOf(PropTypes.string).isRequired,
      })).isRequired,
    }),
  ]),
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MigrationReport;
